import { lazy, Suspense } from 'react';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { MantineEmotionProvider } from '@mantine/emotion';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { theme } from '../config/layout';
import Permission from '../components/auth/Permission';
import Loading from '../components/loading/Loading';
import Layout from '../features/layout/component';
import Callback from './callback/Callback';

const queryClient = new QueryClient();

// Lazy loaded page components (from pages/)
const ProjectPage = lazy(() => import('../pages/ProjectPage'));
const ClientPage = lazy(() => import('../pages/ClientPage'));
const PartnerPage = lazy(() => import('../pages/PartnerPage'));
const AuditPage = lazy(() => import('../pages/AuditPage'));
const UserManagePage = lazy(() => import('../pages/UserManagePage'));
const UserRolePage = lazy(() => import('../pages/UserRolePage'));
const GeneralSettingPage = lazy(() => import('../pages/GeneralSettingPage'));
const AuthSettingPage = lazy(() => import('../pages/AuthSettingPage'));
const IntegrationSettingPage = lazy(() => import('../pages/IntegrationSettingPage'));
const WhitelistSettingPage = lazy(() => import('../pages/WhitelistSettingPage'));
const DashboardPage = lazy(() => import('../pages/DashboardPage'));
const DeploymentPage = lazy(() => import('../pages/DeploymentPage'));
const SurveyBuilderPage = lazy(() => import('../pages/SurveyBuilderPage'));
const SurveyPreviewPage = lazy(() => import('../pages/SurveyPreviewPage'));
const SurveyGenerateLinkPage = lazy(() => import('../pages/SurveyGenerateLinkPage'));
const SurveyDataCollectionPage = lazy(() => import('../pages/SurveyDataCollectionPage'));
const TransactionGroupPage = lazy(() => import('../pages/TransactionGroupPage'));
const DistributionPage = lazy(() => import('../pages/DistributionPage'));
const ContactRulePage = lazy(() => import('../pages/ContactRulePage'));
const MasterDataPage = lazy(() => import('../pages/MasterDataPage'));
const CustomIndexPage = lazy(() => import('../pages/CustomIndexPage'));
const TextAnalyticPage = lazy(() => import('../pages/TextAnalyticPage'));
const CloseloopProjectPage = lazy(() => import('../pages/CloseloopProjectPage'));
const CloseloopCasePage = lazy(() => import('../pages/CloseloopCasePage'));
const CloseloopEscalationPage = lazy(() => import('../pages/CloseloopEscalationPage'));
const CloseloopEscalationSummaryPage = lazy(() => import('../pages/CloseloopEscalationSummaryPage'));
const CloseloopTemplatePage = lazy(() => import('../pages/CloseloopTemplatePage'));
const CloseloopSLAPage = lazy(() => import('../pages/CloseloopSLAPage'));
const UserPage = lazy(() => import('../pages/UserPage'));
const SurveyPage = lazy(() => import('../pages/SurveyPage'));
const TransactionPage = lazy(() => import('../pages/TransactionPage'));
const CloseloopPage = lazy(() => import('../pages/CloseloopPage'));
const QuotaPage = lazy(() => import('../pages/QuotaPage'));
const QuotaCallCenterPage = lazy(() => import('../pages/QuotaCallCenterPage'));
const QuotaResponseLimitPage = lazy(() => import('../pages/QuotaResponseLimitPage'));
const ConsoleLanguagePage = lazy(() => import('../pages/ConsoleLanguagePage'));
const ConsoleLocalePage = lazy(() => import('../pages/ConsoleLocalePage'));
const ConsoleQuotaOverviewPage = lazy(() => import('../pages/ConsoleQuotaOverviewPage'));
const ConsoleQuotaManagedClientPage = lazy(() => import('../pages/ConsoleQuotaManagedClientPage'));
const ConsoleQuotaManagedProjectPage = lazy(() => import('../pages/ConsoleQuotaManagedProjectPage'));

const LazyPage = ({ element: Element }: { element: React.ComponentType<any> }) => (
  <Suspense fallback={<Loading loading={true} />}>
    <Element />
  </Suspense>
);

const MainLayout = () => (
  <Layout type="main">
    <Outlet />
  </Layout>
);

const ManageLayout = () => (
  <Layout type="manage">
    <Outlet />
  </Layout>
);

const ConsoleLayout = () => (
  <Layout type="main" subType="console">
    <Outlet />
  </Layout>
);

export function App() {
  return (
    <MantineProvider theme={theme} withGlobalClasses={false}>
      <Notifications position="top-right" />
      <MantineEmotionProvider>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} />
          <Permission />
          <Routes>
            <Route path="/" element={<Navigate to="/main/project" replace />} />
            <Route path="/callback" element={<Callback />} />

            <Route path="/main" element={<MainLayout />}>
              <Route path="project" element={<LazyPage element={ProjectPage} />} />
              <Route path="client" element={<LazyPage element={ClientPage} />} />
              <Route path="partner" element={<LazyPage element={PartnerPage} />} />
              <Route path="audit" element={<LazyPage element={AuditPage} />} />
              <Route path="user" element={<LazyPage element={UserPage} />} />
              <Route path="user/manage" element={<LazyPage element={UserManagePage} />} />
              <Route path="user/role" element={<LazyPage element={UserRolePage} />} />
              <Route path="setting/general" element={<LazyPage element={GeneralSettingPage} />} />
              <Route path="setting/authentication" element={<LazyPage element={AuthSettingPage} />} />
              <Route path="setting/integration" element={<LazyPage element={IntegrationSettingPage} />} />
              <Route path="setting/whitelist" element={<LazyPage element={WhitelistSettingPage} />} />
            </Route>

            <Route path="/manage" element={<ManageLayout />}>
              <Route path="dashboard" element={<LazyPage element={DashboardPage} />} />
              <Route path="deployment" element={<LazyPage element={DeploymentPage} />} />
              <Route path="survey" element={<LazyPage element={SurveyPage} />} />
              <Route path="survey/builder" element={<LazyPage element={SurveyBuilderPage} />} />
              <Route path="survey/preview" element={<LazyPage element={SurveyPreviewPage} />} />
              <Route path="survey/generate-link" element={<LazyPage element={SurveyGenerateLinkPage} />} />
              <Route path="survey/data-collection" element={<LazyPage element={SurveyDataCollectionPage} />} />
              <Route path="transaction" element={<LazyPage element={TransactionPage} />}>
                <Route index element={<Navigate to="group" replace />} />
                <Route path="group" element={<LazyPage element={TransactionGroupPage} />} />
                <Route path="distribution" element={<LazyPage element={DistributionPage} />} />
                <Route path="contact-rule" element={<LazyPage element={ContactRulePage} />} />
                <Route path="master-data" element={<LazyPage element={MasterDataPage} />} />
                <Route path="custom-index" element={<LazyPage element={CustomIndexPage} />} />
                <Route path="text-analytic" element={<LazyPage element={TextAnalyticPage} />} />
              </Route>
              <Route path="closeloop" element={<LazyPage element={CloseloopPage} />}>
                <Route index element={<Navigate to="case" replace />} />
                <Route path="case" element={<LazyPage element={CloseloopCasePage} />} />
                <Route path="project" element={<LazyPage element={CloseloopProjectPage} />} />
                <Route path="sla" element={<LazyPage element={CloseloopSLAPage} />} />
                <Route path="escalation" element={<LazyPage element={CloseloopEscalationPage} />} />
                <Route path="escalation-summary" element={<LazyPage element={CloseloopEscalationSummaryPage} />} />
                <Route path="template" element={<LazyPage element={CloseloopTemplatePage} />} />
              </Route>
              <Route path="quota" element={<LazyPage element={QuotaPage} />} />
              <Route path="quota/call-center" element={<LazyPage element={QuotaCallCenterPage} />} />
              <Route path="quota/response-limit" element={<LazyPage element={QuotaResponseLimitPage} />} />
            </Route>

            <Route path="/console" element={<ConsoleLayout />}>
              <Route path="language" element={<LazyPage element={ConsoleLanguagePage} />} />
              <Route path="locale" element={<LazyPage element={ConsoleLocalePage} />} />
              <Route path="quota/overview" element={<LazyPage element={ConsoleQuotaOverviewPage} />} />
              <Route path="quota/managed/client" element={<LazyPage element={ConsoleQuotaManagedClientPage} />} />
              <Route path="quota/managed/project" element={<LazyPage element={ConsoleQuotaManagedProjectPage} />} />
            </Route>
          </Routes>
        </QueryClientProvider>
      </MantineEmotionProvider>
    </MantineProvider>
  );
}

export default App;
